import type {KeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import {objectValues, getRandomInt} from '@jetbrains/teamcity-ui/dist/utils'

import {getStatusKey} from '../rest/schemata'
import type {StatusKey} from '../rest/schemata'
import {getExtensionEndpoint} from '../selectors'
import type {Branch} from '../services/rest'
import {restApi} from '../services/rest'
import {getStatusRequest} from '../types'
import type {
  BuildTypeId,
  BuildTypeInternalId,
  ProjectId,
  ProjectInternalId,
  StatusRequest,
  PipelineId,
} from '../types'
import {ActionThrottlerWithObjectCollect} from '../utils/actionThrottler'
import {statusesThrottleInterval} from '../utils/featureToggles'
import {notNull} from '../utils/guards'
import {unsubscribeOnSuccess} from '../utils/refetchable'
import {subscribeOnBuildTypeEvents, subscribeOnProjectEvents} from '../utils/subscriber'
import type {Unsubscribe} from '../utils/subscriber'
import * as SubscriptionEvents from '../utils/subscriptionEvents'

import type {AppThunk} from './types'

const fetchStatuses =
  (requests: KeyValue<StatusKey, StatusRequest>): AppThunk =>
  (dispatch, getState) => {
    const extension = getExtensionEndpoint(getState(), 'fetchStatuses')
    if (extension != null) {
      unsubscribeOnSuccess(
        dispatch(
          restApi.endpoints.getStatuses.initiate({
            endpoint: extension.endpoint,
            requestIds: objectValues(requests).filter(notNull),
          }),
        ),
      )
    }
  }

const MIN_THROTTLE_TIME = 500
const MAX_THROTTLE_TIME = 1000
const STATUSES_THROTTLE =
  statusesThrottleInterval !== -1
    ? statusesThrottleInterval
    : getRandomInt(MIN_THROTTLE_TIME, MAX_THROTTLE_TIME)

const fetchStatusesThrottler = new ActionThrottlerWithObjectCollect(
  fetchStatuses,
  {},
  STATUSES_THROTTLE,
  {leading: false, trailing: true},
)
export const fetchStatusesWithThrottle = (
  statuses: ReadonlyArray<StatusRequest>,
  force?: boolean,
): AppThunk<any> =>
  fetchStatusesThrottler.fetch(
    Object.fromEntries(statuses.map(status => [getStatusKey(status), status])),
    force,
  )

const cancelFetchStatus = (status: StatusRequest) =>
  fetchStatusesThrottler.cancel(getStatusKey(status))

const cancelFetchStatuses = (statuses: ReadonlyArray<StatusRequest>) => {
  statuses.forEach(status => cancelFetchStatus(status))
}

const projectEventTypes = [
  SubscriptionEvents.BUILD_STARTED,
  SubscriptionEvents.BUILD_CHANGES_LOADED,
  SubscriptionEvents.BUILD_CHANGED_STATUS,
  SubscriptionEvents.BUILD_FINISHED,
  SubscriptionEvents.BUILD_INTERRUPTED,
  SubscriptionEvents.BUILD_TYPE_ACTIVE_STATUS_CHANGED,
  SubscriptionEvents.BUILD_TYPE_ADDED_TO_QUEUE,
  SubscriptionEvents.BUILD_TYPE_REMOVED_FROM_QUEUE,
  SubscriptionEvents.BUILD_TYPE_REGISTERED,
  SubscriptionEvents.BUILD_TYPE_UNREGISTERED,
  SubscriptionEvents.CHANGE_ADDED,
  SubscriptionEvents.PROJECT_PERSISTED,
  SubscriptionEvents.PROJECT_RESTORED,
  SubscriptionEvents.BUILD_TYPE_RESPONSIBILITY_CHANGES,
]
export const subscribeOnBuildTypeStatus =
  (
    id: BuildTypeId | undefined,
    internalId: BuildTypeInternalId,
    branch: Branch | null | undefined,
    timeout?: number | undefined,
  ): AppThunk<Unsubscribe> =>
  dispatch => {
    const request = getStatusRequest({type: 'bt', id, branch})
    const unsubscribe = subscribeOnBuildTypeEvents(
      internalId,
      projectEventTypes,
      () => dispatch(fetchStatusesWithThrottle([request], timeout === 0)),
      timeout,
    )
    return () => {
      cancelFetchStatus(request)
      unsubscribe()
    }
  }
export const subscribeOnPipelineStatus =
  (
    id: PipelineId,
    pipelineInternalId: ProjectInternalId,
    branch: Branch | null | undefined,
    timeout: number | undefined,
  ): AppThunk<Unsubscribe> =>
  dispatch => {
    const requests: Array<StatusRequest> = [getStatusRequest({type: 'pipeline', id, branch})]

    const handler = () => {
      dispatch(fetchStatusesWithThrottle(requests, timeout === 0))
    }

    const unsubscribe = subscribeOnProjectEvents(
      pipelineInternalId,
      projectEventTypes,
      handler,
      timeout,
    )
    return () => {
      cancelFetchStatuses(requests)
      unsubscribe()
    }
  }
export const subscribeOnProjectStatus =
  (
    id: ProjectId,
    internalId: ProjectInternalId,
    branch?: Branch | null | undefined,
    timeout?: number | undefined,
  ): AppThunk<Unsubscribe> =>
  dispatch => {
    const requests: Array<StatusRequest> = [getStatusRequest({type: 'project', id, branch})]

    const handler = () => {
      dispatch(fetchStatusesWithThrottle(requests, timeout === 0))
    }

    const unsubscribe = subscribeOnProjectEvents(internalId, projectEventTypes, handler, timeout)
    return () => {
      cancelFetchStatuses(requests)
      unsubscribe()
    }
  }
